import * as React from 'react'
import { Link } from 'react-router'
import {
  AlertTriangle,
  CheckCircle2,
  FileText,
  Loader2,
  Send,
} from 'lucide-react'
import type {
  ProposalDetailDto,
  ProposalVersionDto,
} from '@lib/dto/proposal.dto'
import type {
  ProposalBlock,
  ProposalDocumentDto,
} from '@lib/dto/proposal-document.dto'
import {
  PROSE_SECTION_LABELS,
  type EditableProseSection,
  type ProposalTemplateOverrides,
} from '@lib/dto/proposal-template.dto'
import type { SiteDto } from '@lib/dto/site.dto'
import {
  PROPOSAL_WORKS,
  PROPOSAL_WORK_LABELS,
} from '@lib/enums/proposal-work.enum'
import { SEASON_LABELS } from '@lib/enums/season.enum'
import { formatDate } from '@lib/helpers/format'
import { formatMoneyAmount } from '@lib/helpers/money'
import { describeUnsendable } from '@lib/helpers/sendability'
import { useClient } from '@/hooks/useClient'
import { useOrganization } from '@/hooks/useOrganization'
import { useProposalDocument } from '@/hooks/useProposalDocument'
import { useProposalTemplate } from '@/hooks/useProposalTemplate'
import {
  useDeliverProposalVersion,
  useSendProposalVersion,
  useUpdateProposal,
  useUpdateProposalVersion,
} from '@/hooks/useProposalMutations'
import { errorMessage } from '@/helpers/logger'
import { ProposalStatusBadge } from '@/components/ClientBadges'
import { ProposalPhotos } from '@/components/ProposalPhotos'
import { ProposalWordingEditor } from '@/components/ProposalWordingEditor'
import { SendProposalDialog } from '@/components/SendProposalDialog'
import { NewerVisitNotice } from '@/components/NewerVisitNotice'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

/** The heading the intro prints under when nobody has changed it. */
const DEFAULT_INTRO_HEADING = 'Why this work, and why now'

/** What the wording editor calls each overridable key, for the summary line. */
function overrideLabel(key: keyof ProposalTemplateOverrides): string {
  if (key === 'jobTypeDescriptions') return 'Job type descriptions'
  if (key === 'acceptance') return 'Acceptance'
  const section: EditableProseSection = key
  return PROSE_SECTION_LABELS[section]
}

/** The sections this proposal has changed, by name, in a stable order. */
function changedSections(overrides: ProposalTemplateOverrides): string[] {
  const keys: (keyof ProposalTemplateOverrides)[] = [
    'narrative',
    'exclusions',
    'access',
    'preparation',
    'standards',
    'terms',
    'comparison',
    'assurances',
    'nextSteps',
    'reviews',
    'sinceLastVisit',
    'futureWork',
    'acceptance',
    'jobTypeDescriptions',
  ]
  return keys
    .filter((key) => overrides[key] !== undefined)
    .map(overrideLabel)
}

type SummaryBlock = Extract<ProposalBlock, { type: 'summary' }>

function summaryOf(document: ProposalDocumentDto): SummaryBlock | null {
  return (
    document.blocks.find(
      (block): block is SummaryBlock => block.type === 'summary',
    ) ?? null
  )
}

/** Whether any site on the draft has areas drawn. */
function hasAreas(document: ProposalDocumentDto): boolean {
  return document.blocks.some(
    (block) => block.type === 'siteMap' && (block.areas?.length ?? 0) > 0,
  )
}

/**
 * A `YYYY-MM-DD` date as the END of that day in UTC.
 *
 * The document prints the expiry in UTC, so the end of the UTC day is the
 * instant that prints as the day picked, and it does not lapse on the
 * morning of the date it promises.
 */
function endOfDay(day: string): string {
  return `${day}T23:59:59.000Z`
}

interface ProposalDraftVersionProps {
  proposal: ProposalDetailDto
  version: ProposalVersionDto
  /**
   * Emailing can fail after the version is already stamped sent. The draft
   * then unmounts, so the message has to live on the page that stays.
   */
  onDeliverError: (message: string | null) => void
}

/**
 * The version being written, laid out as what it needs before it can go.
 *
 * A draft has one job — get ready to send — so it reads as that: whether it
 * can go yet, and if not why, then each thing a salesperson fills in, with
 * Preview and Send at the top where the work ends. Everything about a sent
 * version (who opened it, what they chose) is absent, because a draft has
 * none of it yet.
 */
export function ProposalDraftVersion({
  proposal,
  version,
  onDeliverError,
}: ProposalDraftVersionProps) {
  const client = useClient(proposal.clientId)
  const document = useProposalDocument(version.id)
  const template = useProposalTemplate(proposal.id)
  const organization = useOrganization()
  const updateVersion = useUpdateProposalVersion()
  const updateProposal = useUpdateProposal()
  const sendVersion = useSendProposalVersion()
  const deliverVersion = useDeliverProposalVersion()

  const [intro, setIntro] = React.useState<string>(version.summary ?? '')
  // The text the last successful save sent. "Saved" is true only while the
  // field still matches it, so a keystroke during that save cannot inherit it.
  const [savedIntro, setSavedIntro] = React.useState<string | null>(null)
  const [error, setError] = React.useState<string | null>(null)
  const [emailError, setEmailError] = React.useState<string | null>(null)
  const [confirmingSend, setConfirmingSend] = React.useState<boolean>(false)
  const [customizing, setCustomizing] = React.useState<boolean>(false)
  const [wordingOpened, setWordingOpened] = React.useState<boolean>(false)

  const selectedSiteIds = version.sites.map((site) => site.siteId)
  const problem =
    document.data === undefined ? null : describeUnsendable(document.data)
  const summary = document.data === undefined ? null : summaryOf(document.data)
  const showAreaPricing =
    proposal.itemizeByArea ||
    (document.data !== undefined && hasAreas(document.data))
  const narrative = template.data?.resolved.narrative
  const introHeading = narrative?.title ?? DEFAULT_INTRO_HEADING
  const changed =
    template.data === undefined ? [] : changedSections(template.data.overrides)
  const isSending = sendVersion.isPending || deliverVersion.isPending

  async function save(
    input: Parameters<typeof updateVersion.mutateAsync>[0]['input'],
  ): Promise<boolean> {
    setError(null)
    try {
      await updateVersion.mutateAsync({
        versionId: version.id,
        proposalId: proposal.id,
        input,
      })
      return true
    } catch (caught) {
      setError(errorMessage(caught, 'Something went wrong. Please try again.'))
      return false
    }
  }

  async function saveIntro(): Promise<void> {
    if (intro === (version.summary ?? '')) return
    const sent = intro
    const saved = await save({ summary: sent })
    if (saved) setSavedIntro(sent)
  }

  async function saveProposal(
    input: Parameters<typeof updateProposal.mutateAsync>[0]['input'],
  ): Promise<void> {
    setError(null)
    try {
      await updateProposal.mutateAsync({ id: proposal.id, input })
    } catch (caught) {
      setError(errorMessage(caught, 'Something went wrong. Please try again.'))
    }
  }

  async function handleEmail(): Promise<void> {
    setEmailError(null)
    onDeliverError(null)
    try {
      await deliverVersion.mutateAsync({
        versionId: version.id,
        proposalId: proposal.id,
      })
      setConfirmingSend(false)
    } catch (caught) {
      // Left open on failure. The message is inside the dialog: it is modal,
      // so a line on the page behind it cannot be read. The page is told too,
      // because a failure after the version was stamped unmounts this draft.
      const message = errorMessage(caught, 'Something went wrong. Please try again.')
      setEmailError(message)
      onDeliverError(message)
    }
  }

  async function handleMarkSent(): Promise<void> {
    setError(null)
    setConfirmingSend(false)
    try {
      await sendVersion.mutateAsync({
        versionId: version.id,
        proposalId: proposal.id,
      })
    } catch (caught) {
      setError(errorMessage(caught, 'Something went wrong. Please try again.'))
    }
  }

  function toggleSite(siteId: string): void {
    const next = selectedSiteIds.includes(siteId)
      ? selectedSiteIds.filter((id) => id !== siteId)
      : [...selectedSiteIds, siteId]
    void save({ siteIds: next })
  }

  function renderSites(): React.ReactNode {
    const toggles = (sites: SiteChoice[]): React.ReactNode => (
      <SiteToggles
        sites={sites}
        selectedIds={selectedSiteIds}
        onToggle={toggleSite}
        disabled={updateVersion.isPending}
      />
    )
    if (client.isPending) {
      return (
        <p className="flex items-center gap-1.5 text-sm text-muted-text">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Loading this client’s sites…
        </p>
      )
    }
    if (client.isError) {
      return (
        <div className="space-y-2">
          <p className="text-sm text-danger">
            {errorMessage(client.error, 'Sites could not be loaded.')}
          </p>
          {version.sites.length === 0
            ? null
            : toggles(
                version.sites.map((site) => ({
                  id: site.siteId,
                  name: site.name,
                })),
              )}
        </div>
      )
    }
    return toggles(siteChoices(client.data?.sites ?? [], version.sites))
  }

  function introStatus(): string {
    if (updateVersion.isPending && intro !== (version.summary ?? '')) {
      return 'Saving…'
    }
    if (savedIntro !== null && intro === savedIntro) return 'Saved'
    return 'Saves when you click away'
  }

  function validityHint(): React.ReactNode {
    if (version.validUntil !== null) {
      return 'The cover says the price holds until this date.'
    }
    const days = organization.data?.proposalValidityDays ?? null
    if (days !== null) {
      return `Leave blank to use your default: ${String(days)} days after it’s sent.`
    }
    return (
      <>
        Leave blank for no expiry date, or set a default in{' '}
        <Link
          to="/settings?section=pricing"
          className="underline underline-offset-2"
        >
          Settings
        </Link>
        .
      </>
    )
  }

  return (
    <section className="rounded-lg border border-strong bg-surface">
      <header className="flex flex-wrap items-center justify-between gap-2 border-b border-default px-4 py-2.5">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="font-semibold">Version {version.versionNumber}</h2>
          <ProposalStatusBadge status={version.status} />
          <span className="text-xs text-muted-text">
            Started {formatDate(version.createdAt)}
            {version.createdByName ? ` by ${version.createdByName}` : ''}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <Button asChild size="sm" variant="outline">
            <Link
              to={`/proposals/${proposal.id}/versions/${version.id}/document`}
            >
              <FileText className="h-3.5 w-3.5" />
              Preview
            </Link>
          </Button>
          <Button
            size="sm"
            onClick={() => {
              setEmailError(null)
              onDeliverError(null)
              setConfirmingSend(true)
            }}
            // Held back while the check is still running, failed, or says the
            // send would be refused. The reason is the line directly
            // underneath, so nothing is disabled without saying why.
            disabled={
              isSending ||
              document.isPending ||
              document.isError ||
              problem !== null
            }
          >
            {isSending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Send className="h-3.5 w-3.5" />
            )}
            Send
          </Button>
          <SendProposalDialog
            open={confirmingSend}
            onOpenChange={setConfirmingSend}
            versionNumber={version.versionNumber}
            clientName={proposal.clientName}
            recipient={proposal.recipient}
            isEmailing={deliverVersion.isPending}
            isMarking={sendVersion.isPending}
            onEmail={() => {
              void handleEmail()
            }}
            onMarkSent={() => {
              void handleMarkSent()
            }}
            emailError={emailError}
          />
        </div>
      </header>
      <NewerVisitNotice proposal={proposal} version={version} />
      {/*
        A rebid prices one season's work and leaves the rest out on purpose
        (docs/seasonal-work.md). Said here, because a draft missing most of a
        site's trees otherwise looks like a broken survey.
      */}
      {proposal.season === null || proposal.season === undefined ? null : (
        <p className="border-b border-default bg-subtle px-4 py-2 text-sm text-secondary-text">
          A rebid of {SEASON_LABELS[proposal.season].toLowerCase()} work: it
          bids only that season’s work from the visits its sites were surveyed
          on, at today’s rates. Other seasons are left out.
        </p>
      )}

      <Readiness
        isChecking={document.isPending}
        loadError={
          document.isError
            ? errorMessage(
                document.error,
                'This draft could not be checked.',
              )
            : null
        }
        problem={problem}
        summary={summary}
        sites={version.sites}
      />

      {error === null ? null : (
        <p className="border-b border-default px-4 py-2 text-sm text-destructive">
          {error}
        </p>
      )}

      <dl className="divide-y divide-default">
        <Row label="Sites covered">
          {renderSites()}
        </Row>

        <Row label="Work">
          <div
            role="radiogroup"
            aria-label="Work"
            className="inline-flex rounded-md border border-default p-0.5"
          >
            {PROPOSAL_WORKS.map((work) => (
              <Segment
                key={work}
                checked={proposal.work === work}
                disabled={updateProposal.isPending}
                onSelect={() => {
                  void saveProposal({ work })
                }}
              >
                {PROPOSAL_WORK_LABELS[work]}
              </Segment>
            ))}
          </div>
          <p className="mt-1 text-xs text-muted-text">
            What this proposal bids. Irrigation is the open repairs from each
            site’s irrigation survey, priced from Housecall Pro.
          </p>
        </Row>

        <Row label={introHeading} htmlFor={`intro-${version.id}`}>
          <textarea
            id={`intro-${version.id}`}
            rows={4}
            value={intro}
            placeholder={narrative?.paragraphs.join('\n\n') ?? ''}
            onChange={(event) => {
              setIntro(event.target.value)
            }}
            onBlur={() => {
              void saveIntro()
            }}
            // Grows with what is written, so a finished intro can be read
            // whole without scrolling a box inside the page.
            className="flex min-h-24 w-full field-sizing-content rounded-md border border-default bg-surface px-2.5 py-1.5 text-sm text-primary-text transition-colors placeholder:text-disabled-text hover:border-strong focus-visible:outline-none"
          />
          <p className="mt-1 text-xs text-muted-text">
            {intro.trim() === ''
              ? 'Blank uses your standard text, shown in gray. '
              : ''}
            {introStatus()}
          </p>
        </Row>

        <Row label="Photos">
          <ProposalPhotos
            clientId={proposal.clientId}
            attached={version.documents}
            editable
            disabled={updateVersion.isPending}
            onChange={(documentIds) => {
              void save({ documentIds })
            }}
          />
        </Row>

        <Row label="Price valid until" htmlFor={`valid-${version.id}`}>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              id={`valid-${version.id}`}
              type="date"
              className="w-44"
              value={version.validUntil?.slice(0, 10) ?? ''}
              min={new Date().toISOString().slice(0, 10)}
              disabled={updateVersion.isPending}
              onChange={(event) => {
                const day = event.target.value
                void save({ validUntil: day === '' ? null : endOfDay(day) })
              }}
            />
            {version.validUntil === null ? null : (
              <Button
                size="sm"
                variant="ghost"
                disabled={updateVersion.isPending}
                onClick={() => {
                  void save({ validUntil: null })
                }}
              >
                Clear
              </Button>
            )}
          </div>
          <p className="mt-1 text-xs text-muted-text">{validityHint()}</p>
        </Row>

        {showAreaPricing ? (
          <Row label="Pricing">
            <div
              role="radiogroup"
              aria-label="Pricing"
              className="inline-flex rounded-md border border-default p-0.5"
            >
              <Segment
                checked={!proposal.itemizeByArea}
                disabled={updateProposal.isPending}
                onSelect={() => {
                  void saveProposal({ itemizeByArea: false })
                }}
              >
                By site
              </Segment>
              <Segment
                checked={proposal.itemizeByArea}
                disabled={updateProposal.isPending}
                onSelect={() => {
                  void saveProposal({ itemizeByArea: true })
                }}
              >
                By area
              </Segment>
            </div>
            <p className="mt-1 text-xs text-muted-text">
              By area gives each area drawn on the site map its own line in the
              price.
            </p>
          </Row>
        ) : null}

        <Row label="Wording">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm">
              {changed.length === 0 ? (
                <span className="text-secondary-text">
                  Your standard wording from Settings.
                </span>
              ) : (
                <>
                  <span className="text-secondary-text">
                    Changed for this proposal:{' '}
                  </span>
                  {changed.join(', ')}
                </>
              )}
            </p>
            <Button
              size="sm"
              variant="outline"
              aria-expanded={customizing}
              onClick={() => {
                setWordingOpened(true)
                setCustomizing((open) => !open)
              }}
            >
              {customizing ? 'Hide' : 'Customize'}
            </Button>
          </div>
        </Row>
      </dl>

      {/*
        Kept mounted once opened, and hidden rather than removed: closing it
        must not throw away edits somebody has not saved yet.
      */}
      {wordingOpened ? (
        <div hidden={!customizing} className="border-t border-default p-4">
          <ProposalWordingEditor
            embedded
            proposalId={proposal.id}
            title="Wording for this proposal"
            blurb="Changes here apply to this proposal only. Anything left alone uses your standard wording from Settings."
          />
        </div>
      ) : null}
    </section>
  )
}

/**
 * Whether the draft can go, said before anybody presses Send.
 *
 * The same check the API makes at send (`describeUnsendable`), run on the
 * draft's own document, so the reason a send would be refused is on screen
 * while there is still something to do about it.
 */
/** "90 trees", "3 irrigation repairs", or both: what the draft bids. */
function workWords(summary: SummaryBlock): string {
  const trees = `${String(summary.treeCount)} ${summary.treeCount === 1 ? 'tree' : 'trees'}`
  const repairCount = summary.irrigationRepairCount ?? 0
  if (repairCount === 0) return trees
  const repairs = `${String(repairCount)} irrigation ${repairCount === 1 ? 'repair' : 'repairs'}`
  return summary.treeCount === 0 ? repairs : `${trees} and ${repairs}`
}

function Readiness({
  isChecking,
  loadError,
  problem,
  summary,
  sites,
}: {
  isChecking: boolean
  loadError: string | null
  problem: string | null
  summary: SummaryBlock | null
  sites: ProposalVersionDto['sites']
}) {
  if (isChecking) {
    return (
      <p className="flex items-center gap-1.5 border-b border-default px-4 py-2 text-xs text-muted-text">
        <Loader2 className="h-3 w-3 animate-spin" />
        Checking whether it’s ready to send…
      </p>
    )
  }
  if (loadError !== null) {
    return (
      <p className="border-b border-default px-4 py-2 text-sm text-danger">
        {loadError}
      </p>
    )
  }
  if (problem !== null) {
    return (
      <div className="flex flex-wrap items-start gap-x-3 gap-y-1 border-b border-default bg-warning-subtle px-4 py-2 text-sm">
        <span className="flex min-w-0 flex-1 items-start gap-1.5">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning" />
          <span>{problem}</span>
        </span>
        {sites.map((site) => (
          <Link
            key={site.siteId}
            to={`/sites/${site.siteId}/survey`}
            className="text-sm font-medium text-brand underline-offset-2 hover:underline"
          >
            {sites.length === 1 ? 'Open the survey' : `Survey ${site.name}`}
          </Link>
        ))}
      </div>
    )
  }
  if (summary === null) return null
  return (
    <p className="flex items-start gap-1.5 border-b border-default px-4 py-2 text-sm">
      <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-success" />
      <span>
        <span className="font-medium">Ready to send</span>
        <span className="text-muted-text">
          {' · '}
          {workWords(summary)} on {summary.siteCount}{' '}
          {summary.siteCount === 1 ? 'site' : 'sites'}
          {summary.totalAmount === null
            ? ''
            : ` · ${formatMoneyAmount(summary.totalAmount)} before tax`}
        </span>
      </span>
    </p>
  )
}

function Row({
  label,
  htmlFor,
  children,
}: {
  label: string
  htmlFor?: string
  children: React.ReactNode
}) {
  return (
    <div className="grid gap-1.5 px-4 py-3 sm:grid-cols-[10rem_minmax(0,1fr)] sm:gap-4">
      <dt className="text-xs font-medium text-secondary-text sm:pt-1.5">
        {htmlFor === undefined ? label : <label htmlFor={htmlFor}>{label}</label>}
      </dt>
      <dd className="min-w-0">{children}</dd>
    </div>
  )
}

function Segment({
  checked,
  disabled,
  onSelect,
  children,
}: {
  checked: boolean
  disabled: boolean
  onSelect: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={checked}
      disabled={disabled}
      onClick={onSelect}
      className={cn(
        'rounded-sm px-2.5 py-1 text-xs transition-colors disabled:opacity-60',
        checked
          ? 'bg-subtle font-medium text-primary-text'
          : 'text-secondary-text hover:text-primary-text',
      )}
    >
      {children}
    </button>
  )
}

/** A site the draft can include. `name` may already say it is archived. */
interface SiteChoice {
  id: string
  name: string
}

/**
 * Active client sites, plus any site already on the draft that is no longer
 * active, so it can be taken off.
 *
 * An archived site that was never on the draft is not offered. One that is
 * already on it is marked, because leaving it on with no way to see it was
 * how a draft kept a site the client no longer has.
 */
function siteChoices(
  clientSites: readonly SiteDto[],
  versionSites: ProposalVersionDto['sites'],
): SiteChoice[] {
  const active = clientSites.filter((site) => site.archivedAt === null)
  const activeIds = new Set(active.map((site) => site.id))
  const known = new Map(clientSites.map((site) => [site.id, site]))
  const retained = versionSites
    .filter((site) => !activeIds.has(site.siteId))
    .map((site) => {
      const match = known.get(site.siteId)
      const archived = match !== undefined && match.archivedAt !== null
      return {
        id: site.siteId,
        name: archived ? `${site.name} (archived)` : site.name,
      }
    })
  return [
    ...active.map((site) => ({ id: site.id, name: site.name })),
    ...retained,
  ]
}

interface SiteTogglesProps {
  sites: SiteChoice[]
  selectedIds: string[]
  onToggle: (siteId: string) => void
  disabled: boolean
}

function SiteToggles({
  sites,
  selectedIds,
  onToggle,
  disabled,
}: SiteTogglesProps) {
  if (sites.length === 0) {
    return (
      <p className="text-sm text-muted-text">This client has no sites yet.</p>
    )
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {sites.map((site) => {
        const selected = selectedIds.includes(site.id)
        return (
          <button
            key={site.id}
            type="button"
            aria-pressed={selected}
            disabled={disabled}
            onClick={() => onToggle(site.id)}
            className={cn(
              'rounded-md border px-2 py-1 text-xs transition-colors disabled:opacity-60',
              selected
                ? 'border-brand bg-brand-subtle font-medium text-primary-text'
                : 'border-default text-secondary-text hover:border-strong hover:bg-subtle',
            )}
          >
            {site.name}
          </button>
        )
      })}
    </div>
  )
}
